import { Star, Quote } from "lucide-react";
import { TESTIMONIALS, COMPANY } from "../data/site";
import { useReveal } from "../hooks/use-reveal";
import { useSeo } from "../hooks/use-seo";
import {
    localBusinessJsonLd,
    reviewListJsonLd,
    aggregateRatingJsonLd,
    breadcrumbJsonLd,
    SITE_BASE_URL,
} from "../lib/jsonld";
import { ContactForm } from "../components/sections/ContactForm";

export default function Reviews() {
    useReveal();
    useSeo({
        title: "Reviews | HAZ Insulation · 50+ Five-Star Google Reviews",
        description:
            "Read what Chicagoland and NW Indiana homeowners say about HAZ Insulation — 50+ verified five-star reviews for spray foam, attic, crawl space, and dense-pack jobs.",
        canonical: `${SITE_BASE_URL}/reviews`,
        jsonLd: [
            localBusinessJsonLd({ url: `${SITE_BASE_URL}/reviews`, withRating: true }),
            aggregateRatingJsonLd(),
            ...reviewListJsonLd(),
            breadcrumbJsonLd([
                { name: "Home", url: "/" },
                { name: "Reviews", url: "/reviews" },
            ]),
        ],
    });

    return (
        <main data-testid="reviews-page" className="pt-24">
            <section className="py-24 md:py-32 bg-ink text-bone">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-ember mb-5 reveal">
                        <span className="h-px w-10 bg-ember inline-block align-middle mr-3" />
                        Reviews
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold leading-[0.98] tracking-tight reveal">
                        <span className="text-ember">{COMPANY.fiveStarReviews}+</span> five-star
                        reviews <br className="hidden md:block" />
                        in the last 18 months.
                    </h1>
                    <div className="mt-8 flex items-center gap-3 reveal">
                        <div className="flex">
                            {[0, 1, 2, 3, 4].map((i) => (
                                <Star
                                    key={i}
                                    size={22}
                                    className="fill-ember text-ember"
                                    strokeWidth={1.5}
                                />
                            ))}
                        </div>
                        <span className="text-base font-semibold text-bone/85">
                            5.0 avg from verified Google clients
                        </span>
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-28 bg-bone">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {TESTIMONIALS.map((t, i) => (
                            <article
                                key={i}
                                data-testid={`reviews-page-card-${i}`}
                                className="bg-white border border-line p-7 md:p-9 relative reveal"
                            >
                                <Quote
                                    size={28}
                                    strokeWidth={1.5}
                                    className="absolute -top-4 -left-2 text-copper bg-bone p-1"
                                />
                                <div className="flex mb-4">
                                    {Array.from({ length: t.rating }).map((_, k) => (
                                        <Star
                                            key={k}
                                            size={16}
                                            className="fill-ember text-ember"
                                            strokeWidth={1.5}
                                        />
                                    ))}
                                </div>
                                <p className="text-sm md:text-base leading-relaxed text-ink/85">
                                    {t.text}
                                </p>
                                <div className="mt-6 pt-4 border-t border-line">
                                    <div className="font-heading font-bold">{t.name}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted_ink">
                                        {t.location}
                                    </div>
                                    {t.verified && (
                                        <div className="mt-2 inline-block text-[10px] uppercase tracking-[0.18em] font-bold text-moss border border-moss/40 px-2 py-1">
                                            ✓ {t.verified}
                                        </div>
                                    )}
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <ContactForm />
        </main>
    );
}
